// React
import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// React Query
import { useQuery } from "@tanstack/react-query";

// Third Party
import { pdfjs } from "react-pdf";

// Styles
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// API's
import { getPresignedPayPlanPDF } from "@/PayPlans/api/pdf/queries";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Helpers
import { getObjectLink } from "@/Common/helpers/getObjectLink";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import ReportButtonLink from "@/Layouts/components/Buttons/ReportButtonLink";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import StatusHistoryIcon from "@/PayPlans/components/Assignments/StatusHistoryIcon";
import AssignmentMessages from "@/PayPlans/components/Assignments/AssignmentMessages";
import CommonDialog from "@/PayPlans/components/Assignments/CommonDialog";
import ContentDialog from "@PayPlans/components/Assignments/ContentDialog";

// MUI
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { Box, Stack, Typography } from "@mui/material";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import { TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";

// Assets
import HistoryIcon from "@PayPlans/assets/icons/assignmentsStatus/History.svg";
import LegalAudit from "@PayPlans/assets/icons/assignmentsStatus/LegalAudit.svg";
import PDFIcon from "@PayPlans/assets/icons/assignmentsStatus/PDFIcon.svg";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

// Types
type PayPlanHistoryProps = {
	data: {
		acknowledgementDate: string;
	}[];
};

type ArrayProperty = {
	acknowledgementDate: string;
	status: string;
	statusDate: string;
};

type TRowDefinition = {
	row: {
		auditEvidenceLink: string;
		locationId: string;
		position: string;
	};
	value?: string;
	arrayProperty?: ArrayProperty;
};
export default function PayPlanHistory(props: PayPlanHistoryProps) {
	const apiRef = useGridApiRef();

	const { timezone } = useLocation() ?? {};

	// States
	const [open, setOpen] = useState(false);
	const [dialogContent, setDialogContent] = useState("");
	const [rowData, setRowData] = useState<unknown>(null);
	const [dialogClass, setDialogClass] = useState("");
	const [file, setFile] = useState<{ url: string | null } | null>(null);
	const [downloadUrl, setDownloadUrl] = useState<string | null>("");
	const [queryKey, setQueryKey] = useState<string[]>([]);
	const [exportFileName, setExportFileName] = useState("");

	const isQueryKeyAvailable = Boolean(queryKey?.length);

	/********************************************* QUERIES ************************************************/
	const { data: url } = useQuery({
		queryKey: isQueryKeyAvailable ? [...queryKey] : ["presignedUrl"],
		queryFn: async () => {
			if (isQueryKeyAvailable) {
				return getPresignedPayPlanPDF(...queryKey.slice(1));
			}
			return null;
		},
		staleTime: 1000 * 60 * 60, // 1 hour
		enabled: isQueryKeyAvailable,
	});

	const handleClickOpen = async (content: string, rowDefinition: TRowDefinition) => {
		setOpen(true);
		setDialogContent(content);

		if (content === "content1" || content === "content2" || content === "content4") {
			const fileName = content === "content1" ? rowDefinition?.row?.auditEvidenceLink : rowDefinition?.value;
			let pdfType;
			if (content === "content1") {
				pdfType = "audit-evidence";
			} else if (content === "content4") {
				pdfType = "voided";
			} else {
				pdfType = "signed";
			}
			setDialogClass("[&_.MuiPaper-root.MuiDialog-paper]:!w-[930px]");
			setRowData(rowDefinition.arrayProperty);

			setQueryKey(["presignedUrl", rowDefinition?.row?.locationId, rowDefinition?.row?.position, pdfType, fileName ?? ""]);
			setFile(url?.presignedURL);
			setExportFileName(fileName ?? "");
		} else if (content === "content3") {
			setDialogClass("[&_.MuiPaper-root.MuiDialog-paper]:!w-[1000px]");
			setRowData(rowDefinition);
		}
	};

	const getHistoryLastStatus = (row: { acknowledgementStatus?: { status: string; statusDate: string }[] }) => {
		try {
			if (!row.acknowledgementStatus || row.acknowledgementStatus.length === 0) {
				return "COMPLETED";
			}
			return row?.acknowledgementStatus[row?.acknowledgementStatus?.length - 1]?.status;
		} catch (error) {
			console.error("Error getting history last status", error);
		}
		return "COMPLETED";
	};

	const handleClose = () => {
		setOpen(false);
		setDialogContent("");
	};

	const getTitle = () => (dialogContent === "content1" || dialogContent === "content2" ? "Download PDF" : "");

	const getMarginLeft = () => (dialogContent === "content1" ? "40px" : "");

	const dialogTitle =
		dialogContent === "content3" ? (
			<Typography className="!font-montserrat !text-[24px] !font-medium !text-default">Pay Plan Assignment History</Typography>
		) : (
			<ReportButtonLink
				title={getTitle()}
				to={downloadUrl}
				sx={{
					display: "inline-block",
					backgroundColor: "transparent",
					marginLeft: getMarginLeft(),
				}}
				sxButton={{ color: "var(--text-color)" }}
				fileName={exportFileName ? `${exportFileName}.pdf` : null}
			/>
		);

	const columns: GridColDef[] = [
		{ field: "_id", headerName: "ID", filterable: false },
		{ field: "position", headerName: "position", filterable: false },
		{
			field: "planId",
			headerName: "Pay Plan ID",
			width: 130,
			sortable: false,
			filterable: false,
		},
		{
			field: "assignmentDate",
			headerName: "Assignment Date",
			type: "date",
			width: 170,
			sortable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.monthAbbrDatePadded),
		},
		{
			field: "assignedBy",
			headerName: "Assigned By",
			width: 160,
			sortable: false,
		},
		{
			field: "effectiveDate",
			headerName: "Effective Date",
			type: "date",
			width: 220,
			sortable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.monthAbbrDatePadded),
		},
		{
			field: "acknowledgementStatus",
			headerName: "Acknowledgement Status",
			width: 250,
			sortable: false,
			renderCell: (row) => {
				if (!row.value || row.value.length === 0) {
					return null;
				}
				const lastItem = row.value[row.value.length - 1];

				return <AssignmentMessages status={lastItem.status} statusDate={lastItem.statusDate} />;
			},
		},
		{
			field: "acknowledgementDocumentLink",
			filterable: false,
			headerName: "",
			width: 94,
			headerClassName: "table-small-header show-as-column",
			cellClassName: "table-small-cell",
			sortable: false,
			renderCell: (row) => {
				const hasLink = Boolean(row.value);
				const lastStatus = getHistoryLastStatus(row.row);
				return (
					<>
						<Stack justifyContent={"space-between"}>
							<section className="flex items-center justify-between !py-[5px]">
								<StatusHistoryIcon
									icon={LegalAudit}
									alt="icon"
									disabled={!hasLink}
									onClick={() => handleClickOpen("content1", row)}
									tooltipTitle="Audit Trail"
								/>
								{lastStatus === "COMPLETED" && (
									<StatusHistoryIcon
										icon={PDFIcon}
										alt="icon"
										disabled={!hasLink}
										onClick={() => handleClickOpen("content2", row)}
										tooltipTitle="Signed PDF"
									/>
								)}
								{lastStatus === "VOIDED" && (
									<StatusHistoryIcon
										icon={PDFIcon}
										alt="icon"
										disabled={!hasLink}
										onClick={() => handleClickOpen("content4", row)}
										tooltipTitle="Voided PDF"
									/>
								)}
								<StatusHistoryIcon
									icon={HistoryIcon}
									alt="icon"
									disabled={!hasLink}
									onClick={() => handleClickOpen("content3", row)}
									tooltipTitle="History"
								/>
							</section>
						</Stack>

						<CommonDialog
							open={open}
							onClose={handleClose}
							className={clsx({
								"[&_.MuiPaper-root.MuiDialog-paper]:!rounded-[20px]": true,
								"[&_.MuiPaper-root.MuiDialog-paper]:!h-auto": true,
								"[&_.MuiPaper-root.MuiDialog-paper]:!p-[24px]": true,

								[`${dialogClass}`]: true,
							})}
							title={dialogTitle}
						>
							<div className="mt-10" />
							<Box maxHeight={700} overflow="auto">
								{dialogContent && (
									<ContentDialog content={dialogContent} rowData={rowData as object} file={file as unknown as File} />
								)}
							</Box>
						</CommonDialog>
					</>
				);
			},
		},
	];

	React.useEffect(() => {
		const fetchObjectLink = async () => {
			if (url) {
				const blobUrl = await getObjectLink(url?.presignedURL, "application/pdf");
				setFile({ url: blobUrl });
				setDownloadUrl(blobUrl);
			}
		};

		fetchObjectLink();

		return () => {
			setQueryKey([]);
		};
	}, [url]);

	return (
		<DataSmartTable
			columns={columns}
			rows={props?.data || []}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			disableColumnMenu
			disableMultipleRowSelection
			disableRowSelectionOnClick
			disableColumnReorder
			columnVisibilityModel={{
				_id: false,
				position: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					filter: {
						hide: false,
						disabled: false,
					},
					search: {
						data: props?.data || [],
						hide: false,
						disabled: false,
					},
				},
				noRowsOverlay: {
					title: "No Pay Plan Assignment History",
				},
			}}
			hideFooter
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					"functional-table": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.toolbar-actions]:!top-[0px]": true,
				},
				hide: false,
			}}
		/>
	);
}

PayPlanHistory.propTypes = {
	data: PropTypes.arrayOf(
		PropTypes.shape({
			acknowledgementDate: PropTypes.string,
		})
	).isRequired,
};
